import type {
  FlexProps,
  IntrinsicSystemProps,
  MotionPropsFor,
} from '@hover/blueprint';
import {
  Flex,
  Motion,
  forwardRef,
  motion,
  transition,
  useEventListener,
  useTheme,
} from '@hover/blueprint';
import { useInterval } from '@hover/blueprint/chakra';
import { AnimatePresence, useIsPresent } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';

import { preloadImage } from 'src/modules/homeownerApp/helpers';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { useHomeStyles } from '../HomeStyles';

const isInteractiveElement = (element: HTMLElement) =>
  ['a', 'button', 'input'].includes(element.tagName.toLowerCase());

interface PagerProps {
  isPlaying?: boolean;
  size: number;
  expandedSize: number;
}

interface PageIndicatorProps
  extends MotionPropsFor<IntrinsicSystemProps<'button'>>,
    PagerProps {
  isCurrent?: boolean;
  progress?: number;
  exitOrder?: number;
}

const PageIndicator = forwardRef<PageIndicatorProps, 'div'>(
  (
    {
      isPlaying,
      isCurrent,
      progress = 0,
      size,
      expandedSize,
      exitOrder = 0,
      order,
      ...props
    },
    ref,
  ) => {
    const { space, colors } = useTheme();
    const isPresent = useIsPresent();

    const idleOutline = '1px solid transparent';
    const focusOutline = `1px solid ${
      colors.lightAlpha[isCurrent ? 700 : 600]
    }`;

    return (
      <motion.button
        _focusVisible={{ outline: focusOutline }}
        _hover={{ bg: 'lightAlpha.700', '& > div': { bg: 'lightAlpha.900' } }}
        animate={{
          width: isCurrent && isPlaying ? expandedSize : size,
          height: size,
          marginRight: space['50'],
          marginLeft: space['50'],
        }}
        bg="lightAlpha.500"
        border="none"
        borderRadius="round"
        cursor="pointer"
        exit={{
          width: 0,
          height: 0,
          marginRight: '0px',
          marginLeft: '0px',
          outline: idleOutline,
        }}
        height={`${size}px`}
        initial={{ width: 0, height: 0, marginRight: '0px', marginLeft: '0px' }}
        order={isPresent ? order : exitOrder}
        outline={idleOutline}
        outlineOffset="1px"
        overflow="hidden"
        padding="0"
        sx={transition.easing250('background', 'outline')}
        {...props}
        ref={ref}
      >
        <motion.div
          animate={{
            opacity: isCurrent ? 1 : 0,
            width: isPlaying && isCurrent ? `${progress * 100}%` : '100%',
          }}
          bg="lightAlpha.800"
          borderRadius={0}
          height="100%"
          initial={false}
          transition={{ duration: isPlaying ? 0 : 0.25, ease: 'linear' }}
        />
      </motion.button>
    );
  },
);

export interface StylePagerProps
  extends Omit<FlexProps, 'children'>,
    Partial<PagerProps> {
  /**
   * Interval at which to update the progress indicator during auto-play
   */
  interval?: number;
  /**
   * Duration between auto-play style changes in milliseconds
   */
  duration?: number;
  /**
   * Click handler for the current style button
   */
  onToggle?: () => void;
  /**
   * Whether to reset auto-play progress when `isPlaying` changes
   */
  resetOnPause?: boolean;
}

const StylePager = forwardRef<StylePagerProps, 'div'>(
  (
    {
      duration = 5000,
      expandedSize = 28,
      interval = 25,
      isPlaying,
      onToggle,
      resetOnPause = true,
      size = 6,
      ...props
    },
    ref,
  ) => {
    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
    const {
      aspect,
      currentStyle,
      getTabId,
      next,
      nextStyle,
      previous,
      previousStyle,
    } = useHomeStyles();

    const [progress, setProgress] = useState(0);

    const reset = useCallback(() => setProgress(0), []);

    useInterval(
      () => {
        const incremented = progress + 1 / (duration / interval);

        setProgress(incremented);

        if (incremented >= 1) {
          next();
          reset();
        }
      },
      isPlaying ? interval : null,
    );

    useEffect(() => {
      if (!isPlaying && resetOnPause) reset();
    }, [isPlaying, reset, resetOnPause]);

    // Preload next image
    useEffect(() => {
      void preloadImage(nextStyle[aspect].image);
    }, [aspect, nextStyle]);

    // Preload previous image
    useEffect(() => {
      void preloadImage(previousStyle[aspect].image);
    }, [aspect, previousStyle]);

    // Preload opposite aspect (interior/exterior) image
    useEffect(() => {
      void preloadImage(
        currentStyle[aspect === 'exterior' ? 'interior' : 'exterior'].image,
      );
    }, [aspect, currentStyle]);

    useEventListener('keydown', event => {
      const { target } = event;

      // eslint-disable-next-line default-case
      switch (event.key) {
        case 'ArrowLeft':
          if (isPlaying) reset();
          previous();
          return;
        case 'ArrowRight':
          if (isPlaying) reset();
          next();
          return;
        case ' ':
          if (target instanceof HTMLElement && isInteractiveElement(target)) {
            return;
          }

          onToggle?.();
      }
    });

    const indicatorProps = {
      size,
      expandedSize,
      isPlaying,
    } satisfies PageIndicatorProps;

    return (
      <Flex
        alignItems="center"
        direction="row"
        justifyContent="center"
        role="tablist"
        {...props}
        ref={ref}
      >
        <Motion.Firm>
          <AnimatePresence initial={false}>
            <PageIndicator
              {...indicatorProps}
              aria-controls={getTabId(previousStyle)}
              aria-label={t('home.previousStyle')}
              aria-selected="false"
              exitOrder={-2}
              key={previousStyle.name}
              onClick={previous}
              order={-1}
              progress={1}
            />
            <PageIndicator
              {...indicatorProps}
              aria-controls={getTabId(currentStyle)}
              aria-label={t('home.currentStyle')}
              aria-selected="true"
              isCurrent
              key={currentStyle.name}
              onClick={onToggle}
              order={0}
              progress={progress}
            />
            <PageIndicator
              {...indicatorProps}
              aria-controls={getTabId(nextStyle)}
              aria-label={t('home.nextStyle')}
              aria-selected="false"
              exitOrder={2}
              key={nextStyle.name}
              onClick={next}
              order={1}
              progress={0}
            />
          </AnimatePresence>
        </Motion.Firm>
      </Flex>
    );
  },
);

export { StylePager };
